import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { WorkOrderChangeSeasonData } from 'gql/graphql';
import { AddWorkOrderOtherForm } from 'pages/Project/Common/AddWorkOrderOtherForm';
import type { IFormValues } from 'pages/Project/Common/AddWorkOrderOtherForm/helpers';
import { useAddWorkOrderOtherColumns } from 'pages/Project/Common/AddWorkOrderOtherForm/helpers/hooks/useAddWorkOrderOtherColumns';
import { useCheckboxWatcher } from 'pages/Project/Common/AddWorkOrderOtherForm/helpers/hooks/useCheckboxWatcher';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { uerUpdateWorkOrderChangeOtherCostItemsGql } from './addWorkOrderChangeOtherQueries';
import { useAddWorkOrderChangeData } from './hooks';
import { prepareOtherCostData } from './hooks/helpers';

export const AddWorkOrderChangeOther = ({
    onClose,
    workOrderChangeId,
    constructionTypeId,
    reloadWorkOrderTable,
    workOrderChangeSeasons,
}: {
    onClose: () => void;
    workOrderChangeId: number;
    constructionTypeId: number;
    reloadWorkOrderTable: () => void;
    workOrderChangeSeasons: WorkOrderChangeSeasonData[];
}) => {
    const dispatch = useDispatch();

    const {
        control,
        setValue,
        handleSubmit,
        watch,
        formState: { isDirty },
    } = useForm<IFormValues>({
        defaultValues: {
            checkedIds: {},
            otherPriceValues: {},
            otherPriceNotes: {},
        },
    });

    const currentCheckedIds = useWatch({
        name: 'checkedIds',
        control,
    });
    const [isSubmitted, setIsSubmitted] = useState(false);

    const [makeAddOrderOtherReset] = useResetStoreData({ requestId: 'addWorkOrderChangeOtherData' });
    const [makeTableDataReset] = useResetStoreData({ requestId: 'addToWOChangeOther' });

    const onSuccess = useCallback(() => {
        makeAddOrderOtherReset(undefined);
        makeTableDataReset(undefined);
        reloadWorkOrderTable?.();
        onClose?.();
    }, [makeAddOrderOtherReset, makeTableDataReset, onClose, reloadWorkOrderTable]);

    const { groupedData, initialState, visibleRowIds, changeVisibleRowIds, visibleRows, isLoading, nameFilter } =
        useAddWorkOrderChangeData({
            workOrderChangeId,
            constructionTypeId,
        });

    useEffect(() => {
        if (groupedData?.length && !Object.keys(currentCheckedIds)?.length) {
            setValue('checkedIds', initialState?.checkedIds, {
                shouldDirty: false,
                shouldTouch: false,
            });
            setValue('otherPriceValues', initialState?.otherPriceValues, {
                shouldDirty: false,
                shouldTouch: false,
            });
            setValue('otherPriceNotes', initialState?.otherPriceNotes, {
                shouldDirty: false,
                shouldTouch: false,
            });
        }
    }, [
        currentCheckedIds,
        groupedData,
        initialState?.checkedIds,
        initialState?.otherPriceNotes,
        initialState?.otherPriceValues,
        setValue,
    ]);

    useEffect(
        () => () => {
            makeAddOrderOtherReset(undefined);
            makeTableDataReset(undefined);
        },
        [makeAddOrderOtherReset, makeTableDataReset],
    );

    const columns = useAddWorkOrderOtherColumns({
        workOrderSeasons: workOrderChangeSeasons,
        control,
        visibleRowIds,
        changeVisibleRowIds,
        isWorkOrderChange: true,
        nameFilter,
    });

    useCheckboxWatcher({
        watch,
        setValue,
    });

    const [addOtherCostData, addOtherCostStatus, addOtherCostErr, addOtherCostRequest] = useRequest({
        requestId: 'addToWOChangeOther',
        query: uerUpdateWorkOrderChangeOtherCostItemsGql,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });
    const handleSave = useCallback(
        (values) => {
            const addWorkOrderOtherCostVariables = {
                workOrderChangeId,
                constructionTypeId,
                otherPriceInputs: prepareOtherCostData({
                    checkedObjects: values?.checkedIds,
                    priceValues: values?.otherPriceValues,
                    priceNotes: values?.otherPriceNotes,
                }),
            };

            dispatch(addOtherCostRequest(addWorkOrderOtherCostVariables));
            setIsSubmitted(true);
        },
        [addOtherCostRequest, constructionTypeId, dispatch, workOrderChangeId],
    );

    const handleCreateReport = useCallback(() => {
        ExportCSVReport(addOtherCostData?.uerUpdateWorkOrderChangeOtherCostItems?.validation);
    }, [addOtherCostData?.uerUpdateWorkOrderChangeOtherCostItems?.validation]);

    const handleCloseModalError = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const btnConfirmDisabled = useMemo(
        () => !isDirty || !Object.values(currentCheckedIds).some((item) => item?.parent),
        [currentCheckedIds, isDirty],
    );
    const isAddInProgress = addOtherCostStatus === RequestStatus.FETCHING;

    return (
        <>
            <AddWorkOrderOtherForm
                tableColumns={columns}
                tableData={visibleRows}
                isAddInProgress={isAddInProgress}
                isTableLoading={isLoading}
                onConfirm={handleSubmit(handleSave)}
                onCancel={onClose}
                constructionTypeId={constructionTypeId}
                isDirty={isDirty}
                btnConfirmDisabled={btnConfirmDisabled}
                btnConfirmLabel="Добавить в Изм. к НЗ"
            />
            <ErrorHandlingModal
                titleText="Добавить прочие"
                isSubmitted={isSubmitted}
                statusRequest={addOtherCostStatus}
                dataRequest={addOtherCostData?.uerUpdateWorkOrderChangeOtherCostItems}
                errorsRequest={addOtherCostErr}
                handleCloseModalError={handleCloseModalError}
                handleCreateReport={handleCreateReport}
            />
        </>
    );
};

AddWorkOrderChangeOther.displayName = 'AddWorkOrderChangeOther';
